import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export function AuthDialog() {
  const { authOpen, setAuthOpen, authTab, setAuthTab, login, register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reminder, setReminder] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = authTab === "login" ? await login(email, password) : await register(name, email, password, reminder);
      toast.success(`Welcome${authTab === "register" ? " to the movement" : " back"}, ${user.name.split(" ")[0]}.`);
      setAuthOpen(false);
      setPassword("");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="max-h-[92vh] overflow-y-auto bg-[#FFF9EA] border-[#33240E]/25 text-[#33240E] sm:max-w-md" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl" data-testid="auth-dialog-title">Your kindness account</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Sign in to check off daily activations and build your streak toward a Kindness Ball invitation.
          </DialogDescription>
        </DialogHeader>
        <Tabs value={authTab} onValueChange={setAuthTab}>
          <TabsList className="grid w-full grid-cols-2 bg-secondary">
            <TabsTrigger value="login" data-testid="auth-tab-login">Sign In</TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register">Join</TabsTrigger>
          </TabsList>
          <form onSubmit={submit} className="mt-6 space-y-5">
            {authTab === "register" && (
              <TabsContent value="register" forceMount className="mt-0 space-y-5 data-[state=inactive]:hidden">
                <div className="space-y-2">
                  <Label htmlFor="auth-name" className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Name</Label>
                  <Input id="auth-name" data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} required
                    className="rounded-none border-0 border-b border-[#33240E]/25 bg-transparent focus-visible:ring-0 focus-visible:border-[#8A5A0F]" placeholder="Your full name" />
                </div>
              </TabsContent>
            )}
            <div className="space-y-2">
              <Label htmlFor="auth-email" className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input id="auth-email" type="email" data-testid="auth-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="rounded-none border-0 border-b border-[#33240E]/25 bg-transparent focus-visible:ring-0 focus-visible:border-[#8A5A0F]" placeholder="you@example.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="auth-password" className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
              <Input id="auth-password" type="password" data-testid="auth-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                className="rounded-none border-0 border-b border-[#33240E]/25 bg-transparent focus-visible:ring-0 focus-visible:border-[#8A5A0F]" placeholder="6+ characters" />
            </div>
            {authTab === "register" && (
              <label className="flex cursor-pointer items-start gap-3 text-sm font-light text-muted-foreground">
                <input type="checkbox" checked={reminder} onChange={(e) => setReminder(e.target.checked)} data-testid="auth-reminder-checkbox" className="mt-0.5 h-4 w-4 accent-[#33240E]" />
                Email me every morning with that day’s activation so my streak never breaks
              </label>
            )}
            {error && <p className="text-sm text-velvet" data-testid="auth-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="auth-submit-button"
              className="w-full bg-[#33240E] py-3 font-mono text-xs uppercase tracking-[0.25em] text-[#F6E7C1] transition-colors duration-300 hover:bg-[#4A3517] disabled:opacity-50">
              {busy ? "One moment..." : authTab === "login" ? "Sign In" : "Create My Account"}
            </button>
          </form>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
